import React, { ReactNode, useCallback, useRef, useState } from 'react'
import {
  ImageSourcePropType,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  TouchableWithoutFeedback,
  useWindowDimensions
} from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { TokenType } from '@vatom/sdk/core'
import { theme } from '@vatom/wombo'
import { Actionsheet, Box, VStack } from 'native-base'

import { useMaxWidth } from '../../../components/WebWrapper'
import { useKeyboardState } from '../../../hooks/useKeyboardState'
import { ActionSheetCameraScannerUser } from '../Camera/ActionSheetCamera'
import ActionSheetHeader, { HeaderType } from '../partials/ActionSheetHeader'
import SecondaryText from '../partials/SecondaryText'

import RecentList from './partials/RecentList'
import ResultsList from './partials/ResultsList'
import Search from './partials/Search'
import SenderProvider, { useSender } from './partials/SendProvider'

const AnimatedBox = Animated.createAnimatedComponent(Box)

interface ActionSheetSendProps {
  setOpen: (b: boolean) => void
  isOpen: boolean
  label?: ReactNode
  color?: string
  iconSource?: ImageSourcePropType
  token: TokenType
  isShare?: boolean
  contractAddress?: string
  owner?: string
}
export const ActionSheetSend = (props: ActionSheetSendProps) => {
  const { height: screenHeight } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const maxWidth = useMaxWidth()

  const { token, isOpen, setOpen, isShare = false, contractAddress, owner } = props

  if (!isOpen) {
    return null
  }

  return (
    <Actionsheet
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      maxWidth={maxWidth}
      margin={'auto'}
      useRNModal={true}
      // @ts-ignore prop doesn't exit on types
      _backdropFade={{
        entryDelay: 150,
        exitDelay: 10
      }}
    >
      <Box safeAreaTop width="100%">
        <Actionsheet.Content
          _light={{
            backgroundColor: theme.colors.white
          }}
          _dark={{
            backgroundColor: theme.colors.grayDarkMode[900]
          }}
          pt="20px"
          pb="16px"
          px={0}
          maxHeight={screenHeight - insets.top}
          height={screenHeight - insets.top}
        >
          {/* <TouchableWithoutFeedback
            style={{ height: '100%' }}
            onPress={Keyboard.dismiss}
            accessible={false}
          > */}
          <VStack px="16px" w="100%" flex={1}>
            <SenderProvider
              token={token}
              isShare={isShare}
              setOpen={setOpen}
              isOpen={isOpen}
              contractAddress={contractAddress}
              owner={owner}
            >
              <ActionSheetSendWrapped />
            </SenderProvider>
          </VStack>
          {/* </TouchableWithoutFeedback> */}
        </Actionsheet.Content>
      </Box>
    </Actionsheet>
  )
}

export const ActionSheetSendWrapped = () => {
  const sender = useSender()
  const [inputOnFocus, setInputOnFocus] = useState(false)
  const inputDidFocus = useRef(false)

  const handleInputFocus = useCallback((focus: boolean) => {
    if (focus) {
      inputDidFocus.current = true
    }
    setInputOnFocus(() => focus)
  }, [])

  const hideContent = !inputOnFocus && sender.searchString === '' && !inputDidFocus.current

  return (
    <>
      <ActionSheetHeader
        type={HeaderType.close}
        onPress={() => sender.setOpen(false)}
        token={sender.token}
        isShare={sender.isShare}
      />

      {hideContent && (
        <Box marginTop={3} w="100%">
          <SecondaryText hasLearnMore={true} token={sender.token} isShare={sender.isShare} />
        </Box>
      )}

      {hideContent && (
        <AnimatedBox
          marginBottom={5}
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(200)}
        >
          <RecentList />
        </AnimatedBox>
      )}
      <KeyboardAvoidingView
        style={{
          flex: 1
        }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <Search onFocus={() => handleInputFocus(true)} onBlur={() => handleInputFocus(false)} />
        <ActionSheetCameraScannerUser
          isOpen={sender.isScannerOpen}
          setOpen={sender.setIsScannerOpen}
          processLink={sender.onScann}
        />
        {!hideContent && (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(200)}
            style={{
              flex: 1
            }}
          >
            <ResultsList />
          </Animated.View>
        )}
      </KeyboardAvoidingView>
    </>
  )
}
export default ActionSheetSend
